import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import {
  injectQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom, take } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import type {
  AdminUserDetail,
  AdminUserOnboardingStats,
  AdminUsersListQuery,
  ListAdminUsersResponse,
} from '../models/admin-user.model';

const STATS_QUERY_KEY = ['adminUserStats'] as const;
const USERS_QUERY_KEY = ['adminUsers'] as const;
const DEFAULT_PAGE_SIZE = 20;

@Injectable({ providedIn: 'root' })
export class UsersAdminService {
  #http = inject(HttpClient);
  #queryClient = inject(QueryClient);

  page = signal(0);
  pageSize = signal(DEFAULT_PAGE_SIZE);
  roleFilter = signal('');
  onboardingFilter = signal('');
  searchFilter = signal('');
  activeFilter = signal<'all' | 'active' | 'inactive'>('all');

  #statsQuery = injectQuery(() => ({
    queryKey: [...STATS_QUERY_KEY],
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http
          .get<AdminUserOnboardingStats>(
            `${environment.baseUrl}/user/admin/users/stats`,
          )
          .pipe(take(1)),
      ),
  }));

  #usersQuery = injectQuery(() => {
    const query = this.buildListQuery();
    return {
      queryKey: [
        ...USERS_QUERY_KEY,
        this.page(),
        this.pageSize(),
        this.roleFilter(),
        this.onboardingFilter(),
        this.searchFilter(),
        this.activeFilter(),
      ],
      retry: 1,
      queryFn: () => this.fetchUsers(query),
    };
  });

  readonly stats = computed(
    () => this.#statsQuery.data() ?? null,
  );
  readonly statsLoading = computed(() => this.#statsQuery.isLoading());
  readonly statsError = computed(() => this.#statsQuery.isError());

  readonly users = computed(() => this.#usersQuery.data()?.items ?? []);
  readonly totalCount = computed(
    () => this.#usersQuery.data()?.totalCount ?? 0,
  );
  readonly usersLoading = computed(() => this.#usersQuery.isLoading());
  readonly usersError = computed(() => this.#usersQuery.isError());

  async fetchUserDetail(userId: string): Promise<AdminUserDetail> {
    return lastValueFrom(
      this.#http
        .get<AdminUserDetail>(
          `${environment.baseUrl}/user/admin/users/${userId}`,
        )
        .pipe(take(1)),
    );
  }

  setPage(page: number): void {
    this.page.set(Math.max(0, page));
  }

  applyFilters(filters: {
    role: string;
    onboardingStatus: string;
    search: string;
    active: 'all' | 'active' | 'inactive';
  }): void {
    this.roleFilter.set(filters.role);
    this.onboardingFilter.set(filters.onboardingStatus);
    this.searchFilter.set(filters.search);
    this.activeFilter.set(filters.active);
    this.page.set(0);
    void this.#queryClient.invalidateQueries({ queryKey: [...USERS_QUERY_KEY] });
  }

  refresh(): void {
    void this.#queryClient.invalidateQueries({ queryKey: [...STATS_QUERY_KEY] });
    void this.#queryClient.invalidateQueries({ queryKey: [...USERS_QUERY_KEY] });
  }

  private buildListQuery(): AdminUsersListQuery {
    const query: AdminUsersListQuery = {
      limit: this.pageSize(),
      offset: this.page() * this.pageSize(),
    };

    if (this.roleFilter()) {
      query.role = this.roleFilter();
    }
    if (this.onboardingFilter()) {
      query.onboardingStatus = this.onboardingFilter();
    }
    if (this.searchFilter().trim()) {
      query.search = this.searchFilter().trim();
    }
    if (this.activeFilter() === 'active') {
      query.isActive = true;
    } else if (this.activeFilter() === 'inactive') {
      query.isActive = false;
    }

    return query;
  }

  private fetchUsers(query: AdminUsersListQuery): Promise<ListAdminUsersResponse> {
    const params = new URLSearchParams();
    params.set('limit', String(query.limit));
    params.set('offset', String(query.offset));
    if (query.role) {
      params.set('role', query.role);
    }
    if (query.onboardingStatus) {
      params.set('onboardingStatus', query.onboardingStatus);
    }
    if (query.search) {
      params.set('search', query.search);
    }
    if (query.isActive !== undefined) {
      params.set('isActive', String(query.isActive));
    }

    return lastValueFrom(
      this.#http
        .get<ListAdminUsersResponse>(
          `${environment.baseUrl}/user/admin/users?${params.toString()}`,
        )
        .pipe(take(1)),
    );
  }
}
